import moment from "moment";
import React, { useState } from "react";
import { FiArrowLeft, FiCalendar, FiCheck, FiClock, FiShield, FiZap } from "react-icons/fi";
import { FaRupeeSign } from "react-icons/fa";
import ReCAPTCHA from "react-google-recaptcha";
import PhoneNumberContainer from "../../components/PhoneNumberContainer";
import { API_URL, REACT_APP_SITE_KEY } from "../../config/app-config";
import dummyimage from "../../img/default_avatar.jpg";

moment.locale("en");

/* ─── Month calendar grid (same shape/behaviour as V3's MonthGridCalendar) ─── */
function MonthGridCalendar({ slots, selectedValue, onSelect }) {
  const today = moment().locale("en");
  const [viewMonth, setViewMonth] = useState(today.clone().startOf("month"));

  const availableSet = new Set((slots || []).filter((s) => s.available).map((s) => s.value));
  const slotMap = {};
  (slots || []).forEach((s) => {
    slotMap[s.value] = s;
  });

  const goMonth = (dir) =>
    setViewMonth((prev) => prev.clone().locale("en").add(dir, "month").startOf("month"));

  const vmEn = viewMonth.clone().locale("en");
  const startDay = vmEn.clone().startOf("month").day();
  const daysInMonth = vmEn.daysInMonth();

  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));

  const DOW = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
  const isPrevDisabled = viewMonth.clone().locale("en").isSameOrBefore(today.clone().startOf("month"));

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
        <button
          type="button"
          onClick={() => goMonth(-1)}
          disabled={isPrevDisabled}
          style={{
            width: 30, height: 30, borderRadius: 8, border: "1.5px solid #e5e5e5",
            background: "#fff", cursor: isPrevDisabled ? "not-allowed" : "pointer",
            opacity: isPrevDisabled ? 0.4 : 1, fontSize: 16, color: "#161616",
          }}
        >
          ‹
        </button>
        <span style={{ fontSize: 14, fontWeight: 700, color: "#161616" }}>{vmEn.format("MMMM YYYY")}</span>
        <button
          type="button"
          onClick={() => goMonth(1)}
          style={{
            width: 30, height: 30, borderRadius: 8, border: "1.5px solid #e5e5e5",
            background: "#fff", cursor: "pointer", fontSize: 16, color: "#161616",
          }}
        >
          ›
        </button>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4, marginBottom: 6 }}>
        {DOW.map((d) => (
          <div key={d} style={{ textAlign: "center", fontSize: 11, fontWeight: 700, color: "#a0a0a8" }}>
            {d}
          </div>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>
        {weeks.flatMap((week, wi) =>
          week.map((day, di) => {
            if (!day) return <div key={`${wi}-${di}`} />;
            const dateVal = vmEn.clone().date(day).format("YYYY/MM/DD");
            const isPast = vmEn.clone().date(day).isBefore(today, "day");
            const isToday = vmEn.clone().date(day).isSame(today, "day");
            const isAvail = availableSet.has(dateVal) && !isPast;
            const isSelected = selectedValue === dateVal;
            return (
              <button
                key={`${wi}-${di}`}
                type="button"
                disabled={!isAvail}
                onClick={() => {
                  const slotObj = slotMap[dateVal];
                  if (slotObj) onSelect(slotObj);
                }}
                style={{
                  aspectRatio: "1",
                  borderRadius: 10,
                  border: isSelected
                    ? "1.5px solid #161616"
                    : isToday
                      ? "1.5px solid #a0a0a8"
                      : "1.5px solid transparent",
                  background: isSelected ? "#161616" : isAvail ? "#f0fdf4" : "transparent",
                  color: isSelected ? "#fff" : isAvail ? "#161616" : "#d0d0d0",
                  fontSize: 13,
                  fontWeight: isAvail ? 700 : 400,
                  cursor: isAvail ? "pointer" : "not-allowed",
                }}
              >
                {day}
              </button>
            );
          }),
        )}
      </div>
    </div>
  );
}

/* ─── Time slot side panel (same shape as V3's TimeSidePanel) ─── */
function TimeSidePanel({ hasDateSelected, slots, selectedSlot, onSelect }) {
  const formatTime = (str) => {
    if (!str) return "";
    const fmt = str.includes("AM") || str.includes("PM") ? "hh:mm A" : "HH:mm";
    return moment(str, fmt).isValid() ? moment(str, fmt).format("h:mm A") : str;
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 700, color: "#718096", marginBottom: 12 }}>
        <FiClock size={13} /> SELECT TIME
      </div>
      {!hasDateSelected ? (
        <div style={{ textAlign: "center", padding: "40px 12px", color: "#a0a0a8" }}>
          <FiCalendar size={32} style={{ marginBottom: 10, opacity: 0.5 }} />
          <p style={{ fontSize: 13, margin: 0 }}>Pick a date to see available times</p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 320, overflowY: "auto", paddingRight: 4 }}>
          {slots.length > 0 ? (
            slots.map((item, i) => {
              const isSelected = selectedSlot?.from === item.from && selectedSlot?.to === item.to;
              const isDisabled = item.slotAvailable === false;
              return (
                <button
                  key={i}
                  type="button"
                  disabled={isDisabled}
                  onClick={() => !isDisabled && onSelect(item)}
                  style={{
                    padding: "10px 14px",
                    borderRadius: 10,
                    border: `1.5px solid ${isSelected ? "#161616" : "#e5e5e5"}`,
                    background: isSelected ? "#161616" : isDisabled ? "#f5f5f5" : "#fff",
                    color: isSelected ? "#fff" : isDisabled ? "#c0c0c0" : "#161616",
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: isDisabled ? "not-allowed" : "pointer",
                    textAlign: "left",
                  }}
                >
                  {formatTime(item.from)} – {formatTime(item.to)}
                </button>
              );
            })
          ) : (
            <p style={{ fontSize: 13, color: "#a0a0a8" }}>No slots available for this date</p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * QuickBookServiceCard — a focused, single-service booking experience shown
 * whenever a specific serviceId is deep-linked (?serviceId=...). Skips the
 * full profile/catalogue entirely: service info + description up top, a real
 * month calendar + time-slot panel (same interaction model as the V3 theme's
 * booking widget), then a details form and confirm. Independent of whichever
 * profile theme (V2/V3/default) the provider has selected elsewhere.
 */
const QuickBookServiceCard = ({
  data, // full state object from CalendarVcard
  viewAvailableSlot,
  updateState,
  confirmDetails,
  uploadDetails,
  recaptcha,
  onBack,
}) => {
  const service = data.serviceDetails;
  const isMobile = window.screen.width < 768;

  const businessLogo = data.eventDetails?.businessLogo
    ? `${API_URL}auth/image/${data.eventDetails.businessLogo}`
    : dummyimage;

  const priceLabel = !service?.amount || service.amount === 0 ? "Free" : `₹${service?.amount}`;

  const formatTime = (str) => {
    if (!str) return "";
    const fmt = str.includes("AM") || str.includes("PM") ? "hh:mm A" : "HH:mm";
    return moment(str, fmt).isValid() ? moment(str, fmt).format("h:mm A") : str;
  };

  const handleDateSelect = (slotObj) => {
    const idx = (data.slots || []).findIndex((s) => s.value === slotObj.value);
    viewAvailableSlot(idx ?? 0, slotObj, slotObj.available);
  };

  if (!service) return null;

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(180deg, #fafafa 0%, #f0f0ee 100%)",
        display: "flex",
        justifyContent: "center",
        padding: isMobile ? "16px" : "40px 16px",
        fontFamily: "inherit",
      }}
    >
      <div style={{ width: "100%", maxWidth: 980 }}>
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              background: "none",
              border: "none",
              color: "#718096",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              padding: 0,
              marginBottom: 16,
            }}
          >
            <FiArrowLeft size={14} /> Back to profile
          </button>
        )}

        <div
          style={{
            background: "#fff",
            borderRadius: 24,
            boxShadow: "0 8px 32px rgba(0,0,0,0.07)",
            overflow: "hidden",
          }}
        >
          {/* ── Service info header ── */}
          <div style={{ padding: isMobile ? "24px 20px" : "32px 36px", borderBottom: "1px solid #f0f0ee" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 16 }}>
              <img
                src={businessLogo}
                alt="logo"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = dummyimage;
                }}
                style={{ width: 48, height: 48, borderRadius: "50%", objectFit: "cover", border: "2px solid #f0f0f0" }}
              />
              <div>
                <div style={{ fontSize: 13, color: "#a0a0a8" }}>{data.userDetails?.userName}</div>
                <h1 style={{ fontSize: isMobile ? 20 : 24, fontWeight: 700, color: "#161616", margin: 0 }}>
                  {service.serviceName}
                </h1>
              </div>
            </div>

            {service.description && (
              <p style={{ fontSize: 14, color: "#4a5568", lineHeight: 1.65, margin: "0 0 18px", maxWidth: 640 }}>
                {service.description}
              </p>
            )}

            <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 999, background: "#f8f9fa", fontSize: 12, fontWeight: 600, color: "#161616" }}>
                <FiClock size={13} /> {service.duration} min
              </span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 999, background: "#f8f9fa", fontSize: 12, fontWeight: 700, color: "#161616" }}>
                <FaRupeeSign size={11} /> {priceLabel}
              </span>
              {data.eventDetails?.timeZone && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 999, background: "#f8f9fa", fontSize: 12, color: "#718096" }}>
                  <FiCalendar size={13} /> {data.eventDetails.timeZone}
                </span>
              )}
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 999, background: "#fff7ed", fontSize: 12, fontWeight: 600, color: "#c2410c" }}>
                <FiZap size={13} /> Instant confirmation
              </span>
            </div>
          </div>

          {/* ── Booking flow ── */}
          <div style={{ padding: isMobile ? "20px" : "32px 36px" }}>
            {!data.detailsPage ? (
              <>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile ? "1fr" : "1.15fr 1fr",
                    gap: isMobile ? 28 : 40,
                  }}
                >
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#718096", marginBottom: 12 }}>
                      SELECT DATE
                    </div>
                    <MonthGridCalendar
                      slots={data.slots}
                      selectedValue={data.activeIndexData?.value}
                      onSelect={handleDateSelect}
                    />
                  </div>
                  <div>
                    <TimeSidePanel
                      hasDateSelected={!!data.activeIndexData?.value}
                      slots={data.availableSlot || []}
                      selectedSlot={data.selectedSlot}
                      onSelect={(item) => updateState("selectedSlot", item)}
                    />
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => confirmDetails()}
                  disabled={!data.selectedSlot}
                  style={{
                    width: "100%",
                    marginTop: 28,
                    padding: "15px",
                    borderRadius: 14,
                    border: "none",
                    background: data.selectedSlot ? "#161616" : "#e5e5e5",
                    color: data.selectedSlot ? "#fff" : "#a0a0a8",
                    fontSize: 14,
                    fontWeight: 700,
                    cursor: data.selectedSlot ? "pointer" : "not-allowed",
                  }}
                >
                  Continue
                </button>
              </>
            ) : (
              <form onSubmit={uploadDetails} style={{ maxWidth: 480, display: "flex", flexDirection: "column", gap: 14 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <button
                    type="button"
                    onClick={() => updateState("detailsPage", false)}
                    style={{ background: "none", border: "none", cursor: "pointer", padding: 0, fontSize: 20, color: "#161616" }}
                    aria-label="Back"
                  >
                    ←
                  </button>
                  <span style={{ fontSize: 15, fontWeight: 700, color: "#161616" }}>Your details</span>
                </div>

                {data.selectedSlot && data.activeIndexData?.value && (
                  <div
                    style={{
                      background: "#f0fdf4",
                      border: "1px solid #bbf7d0",
                      borderRadius: 12,
                      padding: "12px 16px",
                      fontSize: 13,
                      color: "#166534",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      fontWeight: 600,
                    }}
                  >
                    <FiCalendar size={15} />
                    {moment(data.activeIndexData.value, "YYYY/MM/DD").format("dddd, MMMM D")} · {formatTime(data.selectedSlot.from)}
                  </div>
                )}

                <label style={{ fontSize: 12, fontWeight: 600, color: "#718096" }}>Name *</label>
                <input
                  type="text"
                  required
                  value={data.name}
                  onChange={(e) => updateState("name", e.target.value)}
                  placeholder="Enter your name"
                  style={{ padding: "12px 14px", borderRadius: 10, border: "1.5px solid #e5e5e5", fontSize: 14 }}
                />

                <label style={{ fontSize: 12, fontWeight: 600, color: "#718096" }}>Email *</label>
                <input
                  type="email"
                  required
                  value={data.email}
                  onChange={(e) => updateState("email", e.target.value?.trim())}
                  placeholder="Enter your email"
                  style={{ padding: "12px 14px", borderRadius: 10, border: "1.5px solid #e5e5e5", fontSize: 14 }}
                />

                <label style={{ fontSize: 12, fontWeight: 600, color: "#718096" }}>Phone number *</label>
                <PhoneNumberContainer
                  value={data.phoneNumber}
                  updateData={(v) => updateState("phoneNumber", v)}
                  defaultClass="custom-phone-input"
                />

                <label style={{ fontSize: 12, fontWeight: 600, color: "#718096" }}>
                  Anything to share before the session? *
                </label>
                <textarea
                  required
                  rows={3}
                  value={data.description}
                  onChange={(e) => updateState("description", e.target.value)}
                  placeholder="A few lines about what to cover..."
                  style={{ padding: "12px 14px", borderRadius: 10, border: "1.5px solid #e5e5e5", fontSize: 14, resize: "vertical" }}
                />

                <div style={{ marginTop: 8 }}>
                  <ReCAPTCHA size={isMobile ? "compact" : "normal"} ref={recaptcha} sitekey={REACT_APP_SITE_KEY} />
                </div>

                <button
                  type="submit"
                  style={{
                    marginTop: 8,
                    padding: "15px",
                    borderRadius: 14,
                    border: "none",
                    background: "#161616",
                    color: "#fff",
                    fontSize: 14,
                    fontWeight: 700,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                  }}
                >
                  <FiCheck size={16} /> Confirm booking · {priceLabel}
                </button>

                <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "center", fontSize: 11, color: "#a0a0a8" }}>
                  <FiShield size={12} /> Secure &amp; instant confirmation
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickBookServiceCard;